import React, { useState, useRef } from 'react';
import { parseSpreadsheet, sheetToHtml, isSpreadsheetExtension, type WorkBook } from '@t-one-internal/design-system';
import {
  Upload,
  DotsThreeVertical,
  Download,
  Trash,
  FolderPlus,
  PencilSimple,
  CheckCircle,
  CaretLeft,
  CaretRight,
  CaretDown,
  Check,
  FileText,
  Image as ImageIcon,
  File as FileIcon,
  Folder,
  Lock,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Checkbox,
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@t-one-internal/design-system';
import { showToast } from '@t-one-internal/design-system/lib/components/ui/sonner.tsx';
import { useEmployeeProfileStore } from '../../../stores/employeeProfileStore';
import type { EmployeeDetails } from '../../../stores/employeeProfileStore';
import { useAuth } from '../../../context/AuthContextBackend';
import {
  getDocustorFoldersForDocumentsPage,
  type DocustorFolderUI,
  type DocustorDocumentUI,
  uploadDocumentForDocumentsPage,
  deleteDocustorDoc,
  deleteDocustorFolder,
  downloadDocument,
  renameDocustorFolder,
  createDocustorFolder,
  updateDocustorFolder,
  getFolderPermissions,
  updateFolderPermissions,
  getFolderPermissionForTheUser,
  type RolePermission,
  // moveFile,
  getFolderDocuments,
  getDocumentPreviewUrl,
  getFolderChildren,
  createPrivateSubFolder,
} from '../../../services/docustorService';

interface CellTooltipProps {
  value?: string | null;
  children: React.ReactNode;
}

const CellTooltip: React.FC<CellTooltipProps> = ({ value, children }) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    if (ref.current) {
      setIsTruncated(ref.current.scrollWidth > ref.current.clientWidth);
    }
  }, [value]);

  if (!value || !isTruncated) {
    return (
      <span ref={ref} className="block min-w-0 truncate">
        {children}
      </span>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span ref={ref} className="block min-w-0 truncate cursor-help">
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[400px] break-words">
          {value}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const AVAILABLE_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv',
  'jpg', 'jpeg', 'png', 'svg'
];

type PermissionLevel = 'VIEW_ONLY' | 'CONTRIBUTOR' | 'FULL_CONTROL';

const PERMISSION_LEVEL_CONFIG: Record<PermissionLevel, { label: string; permissions: Omit<RolePermission, 'role_type'> }> = {
  VIEW_ONLY: {
    label: 'View Only',
    permissions: { can_create: false, can_read: true, can_update: false, can_delete: false },
  },
  CONTRIBUTOR: {
    label: 'Contributor',
    permissions: { can_create: true, can_read: true, can_update: false, can_delete: false },
  },
  FULL_CONTROL: {
    label: 'Full Control',
    permissions: { can_create: true, can_read: true, can_update: true, can_delete: true },
  },
};

const PERMISSION_LEVELS: PermissionLevel[] = ['VIEW_ONLY', 'CONTRIBUTOR', 'FULL_CONTROL'];

const derivePermissionLevel = (perm: RolePermission): PermissionLevel => {
  if (perm.can_create && perm.can_read && perm.can_update && perm.can_delete) return 'FULL_CONTROL';
  if (perm.can_create && perm.can_read) return 'CONTRIBUTOR';
  return 'VIEW_ONLY';
};

const DEFAULT_PERMISSIONS_MATRIX: RolePermission[] = [
  { role_type: 'HRMANAGER', can_create: true, can_read: true, can_update: true, can_delete: true },
  { role_type: 'MANAGER', can_create: true, can_read: true, can_update: false, can_delete: false },
  { role_type: 'EXECUTIVE', can_create: false, can_read: true, can_update: false, can_delete: false },
  { role_type: 'EMPLOYEE', can_create: false, can_read: true, can_update: false, can_delete: false },
];

const ROLE_DISPLAY_NAMES: Record<string, string> = {
  HRMANAGER: 'HR Manager',
  MANAGER: 'Manager',
  EXECUTIVE: 'Executive',
  EMPLOYEE: 'Employee',
};

interface DocumentsSectionProps {
  employee: EmployeeDetails;
}

const DocumentsSection: React.FC<DocumentsSectionProps> = ({ employee: _employee }) => {
  const { selectedEmployee } = useEmployeeProfileStore();
  const { user } = useAuth();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<string>('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null);
  const [showMoveMenu, setShowMoveMenu] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [isRenameFolderOpen, setIsRenameFolderOpen] = useState(false);
  const [isEditFolderOpen, setIsEditFolderOpen] = useState(false);
  const [editFolderForm, setEditFolderForm] = useState({
    name: '',
    description: '',
    max_file_size_mb: 20,
    allowed_extensions: [] as string[],
    permissions: DEFAULT_PERMISSIONS_MATRIX as RolePermission[],
  });
  const [showExtensionDropdown, setShowExtensionDropdown] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isDeleteFolderOpen, setIsDeleteFolderOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<{ id: string; name: string } | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [docToDelete, setDocToDelete] = useState<{ id: string; folderId: string; name: string } | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [openFolderMenu, setOpenFolderMenu] = useState<boolean>(false);
  const [previewDocument, setPreviewDocument] = useState<DocustorDocumentUI | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // Subfolder navigation stack: [{id, uuid, name, is_private, folder_type}, ...]
  const [folderStack, setFolderStack] = useState<Array<{ id: string, uuid: string, name: string, is_private?: boolean, folder_type?: string }>>([]);
  const [childFolders, setChildFolders] = useState<DocustorFolderUI[]>([]);
  const [isCreateSubFolderOpen, setIsCreateSubFolderOpen] = useState(false);
  const [newSubFolderName, setNewSubFolderName] = useState('');
  const [isSubFolderPrivate, setIsSubFolderPrivate] = useState(false);
  const [newFolderIsPrivate, setNewFolderIsPrivate] = useState(true);
  const [newFolderPermissions, setNewFolderPermissions] = useState<RolePermission[]>(DEFAULT_PERMISSIONS_MATRIX);
  const [newFolderMaxFileSizeMb, setNewFolderMaxFileSizeMb] = useState(20);
  const [newFolderAllowedExtensions, setNewFolderAllowedExtensions] = useState<string[]>([]);
  const [showNewFolderExtensionDropdown, setShowNewFolderExtensionDropdown] = useState(false);
  // Cache folder info so deep navigation doesn't lose parent info when childFolders is replaced
  const folderInfoMapRef = useRef<Record<string, DocustorFolderUI>>({});

  // RBAC State
  // const [folderPermissions, setFolderPermissions] = useState<RolePermission[]>([]);
  const [canCreateFolder, setCanCreateFolder] = useState(false);
  const [canRenameFolder, setCanRenameFolder] = useState(false);
  const [canDeleteFolder, setCanDeleteFolder] = useState(false);
  const [canDeleteDocuments, setCanDeleteDocuments] = useState(false);
  const [canUpload, setCanUpload] = useState(false);

  // Upload modal state
  const [uploadData, setUploadData] = useState({
    name: '',
    folderId: '' as string,
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [, setIsUploading] = useState(false);
  // const [isDragging, setIsDragging] = useState(false);

  // Anti-rage-click guards
  const [isBusy, setIsBusy] = useState(false);
  const isBusyRef = useRef(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const moveMenuRef = useRef<HTMLDivElement>(null);

  const safeAction = async (action: () => Promise<void>) => {
    if (isBusyRef.current) return;
    isBusyRef.current = true;
    setIsBusy(true);
    try {
      await action();
    } finally {
      setIsBusy(false);
      isBusyRef.current = false;
    }
  };

  // ── Navigation helpers ──
  // Server-sourced folders and documents
  const [foldersFromServer, setFoldersFromServer] = useState<DocustorFolderUI[]>([]);
  const [documents, setDocuments] = useState<DocustorDocumentUI[]>([]);
  const [, setLoading] = useState<boolean>(false);
  const [, setError] = useState<string | null>(null);

  const currentFolderInfo = React.useMemo(() => {
    if (selectedFolder === 'all') return null;
    // Check in root folders first, then in child folders, then cache
    const fromRoot = foldersFromServer.find(f => String(f.folder_id) === selectedFolder);
    if (fromRoot) return fromRoot;
    const fromChild = childFolders.find(f => String(f.folder_id) === selectedFolder);
    if (fromChild) return fromChild;
    // Check the folder info cache (persists across child folder replacements)
    const fromCache = folderInfoMapRef.current[selectedFolder];
    if (fromCache) return fromCache;
    // Fallback to stack info if available
    const fromStack = folderStack.find(f => f.id === selectedFolder);
    if (fromStack) {
      return {
        folder_id: fromStack.id,
        folder_uuid: fromStack.uuid,
        name: fromStack.name,
        is_private: fromStack.is_private,
        folder_type: fromStack.folder_type,
        document_count: 0,
      } as DocustorFolderUI;
    }
    return null;
  }, [selectedFolder, foldersFromServer, childFolders, folderStack]);

  // Helper to determine display name
  const getFolderDisplayName = (folder: DocustorFolderUI) => {
    if (folder.description === 'shared system - hr') return 'Shared with HR';
    if (folder.description === 'shared system - manager') return 'Shared with Manager';
    return folder.name;
  };

  const navigateIntoFolder = (folder: DocustorFolderUI) => {
    const displayName = getFolderDisplayName(folder);
    setFolderStack(prev => [...prev, {
      id: String(folder.folder_id),
      uuid: String(folder.folder_uuid || folder.folder_id),
      name: displayName,
      is_private: folder.is_private,
      folder_type: folder.folder_type,
    }]);
    setSelectedFolder(String(folder.folder_id));
    setSearchTerm('');
  };

  const navigateToStackIndex = (index: number) => {
    if (index < 0) {
      setFolderStack([]);
      setSelectedFolder('all');
    } else {
      const newStack = folderStack.slice(0, index + 1);
      setFolderStack(newStack);
      setSelectedFolder(newStack[newStack.length - 1].id);
    }
    setSearchTerm('');
  };

  // ── Fetch child folders AND documents when navigating into a folder ──
  React.useEffect(() => {
    if (selectedFolder === 'all') {
      setChildFolders([]);
      return;
    }
    // Look up folder info from root folders, current children, or the cache
    const info = foldersFromServer.find(f => String(f.folder_id) === selectedFolder)
      || childFolders.find(f => String(f.folder_id) === selectedFolder)
      || folderInfoMapRef.current[selectedFolder];
    const folderUUID = info?.folder_uuid || info?.folder_id || selectedFolder;

    // Cache current folder info for future deep lookups
    if (info) {
      folderInfoMapRef.current[String(info.folder_id)] = info;
    }

    const fetchChildrenAndDocs = async () => {
      try {
        // Fetch child folders and documents in parallel
        const [childRes, docsRes] = await Promise.all([
          getFolderChildren(String(folderUUID)),
          getFolderDocuments(String(folderUUID))
        ]);

        const children: DocustorFolderUI[] = (childRes?.data || []).map((f: any) => ({
          folder_id: f.id ?? f.folder_id,
          folder_uuid: f.folder_uuid,
          name: f.name,
          document_count: 0,
          allowed_extensions: f.allowed_extensions || [],
          max_file_size_mb: f.max_file_size_mb || 20,
          is_private: f.is_private || f.folder_type === 'PRIVATE',
          folder_type: f.folder_type || (f.is_private ? 'PRIVATE' : 'ORGANIZATION'),
          rec_user_id: f.rec_user_id,
          created_by: undefined,
          parent_folder_id: f.parent_folder_id ? String(f.parent_folder_id) : null,
          id: f.id
        }));

        // Cache all children for future lookups
        children.forEach(c => {
          folderInfoMapRef.current[String(c.folder_id)] = c;
        });

        setChildFolders(children);

        // Merge fetched docs into documents state for this subfolder
        const newDocs: DocustorDocumentUI[] = (docsRes?.data || []).map((d: any) => ({
          doc_id: d.doc_uuid,
          name: d.name ?? d.document_name ?? d.original_filename,
          folder_id: selectedFolder,
          file_type: d.file_type ?? '',
          file_extension: d.file_extension?.Valid ? d.file_extension.String : (d.file_extension || ''),
          file_size_bytes: d.file_size_bytes ?? 0,
          created_at: d.document_created_at ?? d.created_at,
          created_by: d.created_by
        }));

        // Replace docs for this folder (remove old ones, add new)
        setDocuments(prev => {
          const withoutCurrent = prev.filter(d => String(d.folder_id) !== selectedFolder);
          return [...withoutCurrent, ...newDocs];
        });
      } catch (err) {
        console.warn('Failed to fetch child folders/docs', err);
        setChildFolders([]);
      }
    };
    fetchChildrenAndDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder]);

  const fetchDocuments = React.useCallback(async () => {
    if (!selectedEmployee) return;

    try {
      setLoading(true);
      const { folders, documents } = await getDocustorFoldersForDocumentsPage();

      const mappedFolders = folders.map(f => ({
        ...f,
        folder_id: String(f.folder_id),
      }));
      setFoldersFromServer(mappedFolders);

      // Cache root folder info for deep navigation lookups
      mappedFolders.forEach(f => {
        folderInfoMapRef.current[String(f.folder_id)] = f;
      });

      setDocuments(
        documents.map(d => ({
          ...d,
          folder_id: String(d.folder_id),
          doc_id: String(d.doc_id),
        }))
      );
    } catch (e: any) {
      setError(e?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [selectedEmployee]);

  // Auto-dismiss delete error after 4 seconds
  React.useEffect(() => {
    if (deleteError) {
      const timer = setTimeout(() => setDeleteError(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [deleteError]);

  // Fetch document counts for root folders (backend doesn't provide document_count)
  const folderCountsFetchedRef = useRef<string>('');
  React.useEffect(() => {
    if (foldersFromServer.length === 0) return;

    // Build a key from folder IDs to avoid re-fetching when only counts changed
    const folderKey = foldersFromServer.map(f => f.folder_id).sort().join(',');
    if (folderCountsFetchedRef.current === folderKey) return;
    folderCountsFetchedRef.current = folderKey;

    const fetchCounts = async () => {
      const results = await Promise.allSettled(
        foldersFromServer.map(f =>
          getFolderDocuments(String(f.folder_uuid || f.folder_id))
            .then(res => ({ folderId: String(f.folder_id), count: res?.data?.length ?? 0 }))
        )
      );

      setFoldersFromServer(prev =>
        prev.map(f => {
          const result = results.find((r): r is PromiseFulfilledResult<{ folderId: string; count: number }> =>
            r.status === 'fulfilled' && r.value.folderId === String(f.folder_id)
          );
          return result ? { ...f, document_count: result.value.count } : f;
        })
      );
    };

    fetchCounts();
  }, [foldersFromServer]);

  const uploadTargetFolder = React.useMemo(() => {
    if (!uploadData.folderId) return null;
    return foldersFromServer.find(f => String(f.folder_id) === String(uploadData.folderId))
      || childFolders.find(f => String(f.folder_id) === String(uploadData.folderId))
      || folderInfoMapRef.current[String(uploadData.folderId)]
      || null;
  }, [foldersFromServer, childFolders, uploadData.folderId]);

  const acceptedFileTypes = React.useMemo(() => {
    if (uploadTargetFolder?.allowed_extensions?.length) {
      return uploadTargetFolder.allowed_extensions.map(ext => `.${ext.replace(/^\./, '')}`).join(',');
    }
    return ".pdf,.doc,.docx,.odt,.txt,.rtf,.jpg,.jpeg,.png,.xls,.xlsx,.ods,.csv,.ppt,.pptx";
  }, [uploadTargetFolder]);

  // Filter documents based on search and folder
  const filteredDocuments = (Array.isArray(documents) ? documents : []).filter(doc => {
    const matchesSearch = (doc.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFolder = selectedFolder === 'all' || String(doc.folder_id) === selectedFolder;
    return matchesSearch && matchesFolder;
  });

  // Pagination
  const totalPages = Math.ceil(filteredDocuments.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedDocuments = filteredDocuments.slice(startIndex, startIndex + rowsPerPage);

  // File type icon helpers
  const extractFileExtension = (value: unknown, filename?: string): string => {
    // If value is missing or is "application/octet-stream", try filename
    let valStr = '';

    if (value && typeof value === 'object') {
      const v: any = value;
      if (typeof v.String === 'string' && v.String) {
        valStr = v.String;
      }
    } else if (typeof value === 'string') {
      valStr = value;
    }

    // Check if we should use filename fallback
    if (!valStr || valStr.toLowerCase().includes('octet-stream')) {
      if (filename) {
        // Extract extension from filename (e.g. "image.png" -> "png")
        const parts = filename.split('.');
        if (parts.length > 1) {
          return parts.pop()?.toLowerCase() || '';
        }
      }
      return '';
    }

    const cleaned = valStr.startsWith('.') ? valStr.slice(1) : valStr;
    return (cleaned.includes('/') ? (cleaned.split('/').pop() || '') : cleaned).toLowerCase();
  };

  const getFileIcon = (type: unknown, filename?: string) => {
    const ext = extractFileExtension(type, filename);
    const isImage = ['png', 'jpg', 'jpeg'].includes(ext);
    const isPdf = ext === 'pdf';
    const isWord = ['doc', 'docx', 'odt'].includes(ext);
    const isText = ['txt', 'rtf'].includes(ext);
    const isSheet = ['xls', 'xlsx', 'ods', 'csv'].includes(ext);
    const isPpt = ['ppt', 'pptx'].includes(ext);

    const color = isPdf
      ? 'text-red-500'
      : isWord
        ? 'text-blue-500'
        : isSheet
          ? 'text-green-500'
          : isPpt
            ? 'text-orange-500'
            : isImage
              ? 'text-blue-500'
              : isText
                ? 'text-gray-500'
                : 'text-gray-500';

    if (isImage) {
      return <ImageIcon size={16} className={color} weight="regular" />;
    }
    if (isPdf || isWord || isSheet || isPpt || isText) {
      return <FileText size={16} className={color} weight="regular" />;
    }
    return <FileIcon size={16} className={color} weight="regular" />;
  };

  const getFileTypeLabel = (type: unknown, filename?: string) => {
    const ext = extractFileExtension(type, filename);
    return ext ? ext.toUpperCase() : '-';
  };

  const formatISODateToDMY = (value?: unknown) => {
    if (value == null) return '-';
    if (typeof value === 'object' && value) {
      const v: any = value;
      if (typeof v.Time === 'string' && v.Time) {
        value = v.Time as string;
      }
    }
    const raw = typeof value === 'string' ? value : (typeof (value as any).toString === 'function' ? (value as any).toString() : '');
    if (!raw) return '-';
    const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
    if (match) {
      const dt = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      return `${dt.getDate()}-${dt.toLocaleString('en-US', { month: 'short' })}-${dt.getFullYear()}`;
    }
    const dt = new Date(raw);
    if (!isNaN(dt.getTime())) {
      return `${dt.getDate()}-${dt.toLocaleString('en-US', { month: 'short' })}-${dt.getFullYear()}`;
    }
    return '-';
  };

  const getCreationDate = (doc: DocustorDocumentUI) => formatISODateToDMY(doc.created_at);

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (uploadTargetFolder?.allowed_extensions?.length) {
        const fileExt = file.name.split('.').pop()?.toLowerCase();
        const isAllowed = uploadTargetFolder.allowed_extensions.some(ext => ext.toLowerCase().replace(/^\./, '') === fileExt);
        if (!isAllowed) {
          setError(`Invalid file type. Allowed: ${uploadTargetFolder.allowed_extensions.map(e => e.toUpperCase()).join(', ')}`);
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
      }
      setError(null);
      setUploadFile(file);
      setUploadData(prev => ({ ...prev, name: file.name }));
    }
  };

  // Handle drag and drop
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // setIsDragging(false);
    if (isBusy) return;
    const file = event.dataTransfer.files?.[0];
    if (file) {
      if (uploadTargetFolder?.allowed_extensions?.length) {
        const fileExt = file.name.split('.').pop()?.toLowerCase();
        const isAllowed = uploadTargetFolder.allowed_extensions.some(ext => ext.toLowerCase().replace(/^\./, '') === fileExt);
        if (!isAllowed) {
          setError(`Invalid file type. Allowed: ${uploadTargetFolder.allowed_extensions.map(e => e.toUpperCase()).join(', ')}`);
          return;
        }
      }
      setError(null);
      setUploadFile(file);
      setUploadData(prev => ({ ...prev, name: file.name }));
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isBusy) {
      // setIsDragging(true); // removed state
    }
  };

  // Handle document upload
  const handleUpload = async () => {
    if (isBusyRef.current || !uploadFile || !uploadData.folderId) return;

    safeAction(async () => {
      const targetFolderId = uploadData.folderId;
      if (!targetFolderId) throw new Error('Select a folder');

      // Resolve folder_uuid (snowflake) from numeric folder_id
      const folderInfo = foldersFromServer.find(f => String(f.folder_id) === targetFolderId)
        || childFolders.find(f => String(f.folder_id) === targetFolderId)
        || folderInfoMapRef.current[targetFolderId];
      const resolvedUUID = folderInfo?.folder_uuid || targetFolderId;

      setLoading(true);
      setIsUploading(true);

      try {
        const created = await uploadDocumentForDocumentsPage({
          folderUUID: resolvedUUID,
          file: uploadFile,
          customName: uploadData.name,
        });

        // Refetch documents for the current folder instead of root-level fetchDocuments()
        // fetchDocuments() replaces all docs with root-level data which doesn't include per-folder docs in V2
        const response = await getFolderDocuments(String(resolvedUUID));
        if (response?.data) {
          const freshDocs: DocustorDocumentUI[] = response.data.map((d: any) => ({
            doc_id: d.doc_uuid,
            name: d.document_name ?? d.original_filename ?? d.name,
            folder_id: targetFolderId,
            file_type: d.file_extension ?? d.file_type ?? '',
            file_extension: d.file_extension ?? '',
            file_size_bytes: d.file_size_bytes ?? 0,
            created_at: d.document_created_at ?? d.created_at,
            created_by: d.created_by,
          }));
          setDocuments(freshDocs);
          // Update folder document count so root grid shows correct number
          const newCount = freshDocs.length;
          setFoldersFromServer(prev =>
            prev.map(f => String(f.folder_id) === targetFolderId ? { ...f, document_count: newCount } : f)
          );
        } else {
          // Fallback: optimistic add
          setDocuments(prev => [created, ...prev]);
        }

        setShowUploadModal(false);
        setUploadData({ name: '', folderId: uploadData.folderId });
        setUploadFile(null);
        showToast.success('Document uploaded successfully');
      } catch (e: any) {
        const message =
          e?.response?.data?.message ||
          e?.message ||
          'Upload failed due to an unexpected error.';

        showToast.error(message);
        setShowUploadModal(false);
      } finally {
        setLoading(false);
        setIsUploading(false);
        setUploadData({ name: '', folderId: uploadData.folderId });
        setUploadFile(null);
      }
    });
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setUploadData({ name: '', folderId: uploadData.folderId || (foldersFromServer[0] ? String(foldersFromServer[0].folder_id) : '') });
    setUploadFile(null);
    // setIsDragging(false);
  };

  const handleDownload = async (documentId: string, filename: string) => {
    try {
      await downloadDocument(documentId, filename);
    } catch (e) {
      console.error(e);
    }
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;

    try {
      await deleteDocustorDoc(docToDelete.id, docToDelete.folderId);
      setDocuments(prev => {
        const updated = prev.filter(d => String(d.doc_id) !== String(docToDelete.id));
        // Update folder document count
        const folderDocCount = updated.filter(d => String(d.folder_id) === docToDelete.folderId).length;
        setFoldersFromServer(prevFolders =>
          prevFolders.map(f => String(f.folder_id) === docToDelete.folderId ? { ...f, document_count: folderDocCount } : f)
        );
        return updated;
      });
      showToast.success('Document deleted successfully');
      setDocToDelete(null);
      setIsDeleteConfirmOpen(false);
    } catch (e: any) {
      console.error('[DOCUSTOR_DELETE] Delete failed:', e?.response?.status, e?.response?.data);
      const data = e?.response?.data;
      const message = (typeof data === 'string' ? data : (data?.error || data?.message)) || (e?.response?.status === 403 ? 'You do not have permission to delete this file' : 'Failed to delete document');
      console.log('[DOCUSTOR_DELETE] Setting deleteError to:', message);
      console.log('[DOCUSTOR_DELETE] isDeleteConfirmOpen before:', isDeleteConfirmOpen);
      setIsDeleteConfirmOpen(false);
      setDocToDelete(null);
      setDeleteError(message);
      console.log('[DOCUSTOR_DELETE] setDeleteError called');
    }
  };

  const handleDelete = async (documentId: string, folderId: string, documentName: string) => {
    setDocToDelete({ id: documentId, folderId, name: documentName });
    setIsDeleteConfirmOpen(true);
    setShowActionsMenu(null);
  };

  // const handleMove = async (documentId: string, targetFolderId: string) => { ... } removed

  const handlePreview = (doc: DocustorDocumentUI) => {
    setPreviewDocument(doc);
    setIsPreviewModalOpen(true);
  };

  // Helper for file size
  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const PreviewModal = () => {
    const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
    const [spreadsheetHtml, setSpreadsheetHtml] = useState<string | null>(null);
    const [sheetNames, setSheetNames] = useState<string[]>([]);
    const [activeSheet, setActiveSheet] = useState<string>('');
    const workbookRef = useRef<WorkBook | null>(null);
    const { token } = useAuth(); // Get token from context

    // Determine file type
    const isImage = previewDocument?.file_extension && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(previewDocument.file_extension.toLowerCase());
    const isPDF = previewDocument?.file_extension === 'pdf';
    const isText = previewDocument?.file_extension && ['txt', 'csv'].includes(previewDocument.file_extension.toLowerCase());
    const isSpreadsheet = previewDocument?.file_extension && isSpreadsheetExtension(previewDocument.file_extension);

    // Switch sheet tab
    const handleSheetChange = (sheetName: string) => {
      if (!workbookRef.current) return;
      setActiveSheet(sheetName);
      setSpreadsheetHtml(sheetToHtml(workbookRef.current, sheetName));
    };

    // Fetch blob URL whenever the document changes
    React.useEffect(() => {
      if (!previewDocument?.doc_id || !token) return;

      let objectUrl: string | null = null;

      const fetchPreview = async () => {
        try {
          const response = await fetch(
            getDocumentPreviewUrl(previewDocument.doc_id),
            {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: isPDF ? 'application/pdf' : '*/*',
              },
            }
          );

          if (!response.ok) throw new Error('Preview failed');

          const blob = await response.blob();

          if (isSpreadsheet) {
            const arrayBuffer = await blob.arrayBuffer();
            const result = parseSpreadsheet(arrayBuffer);
            workbookRef.current = result.workbook;
            setSheetNames(result.sheetNames);
            setActiveSheet(result.sheetNames[0]);
            setSpreadsheetHtml(result.activeSheetHtml);
          } else {
            // For PDF, we need to ensure correct type
            const type = isPDF ? 'application/pdf' : blob.type;
            const finalBlob = new Blob([blob], { type });
            objectUrl = URL.createObjectURL(finalBlob);
            setPreviewBlobUrl(objectUrl);
          }
        } catch (err) {
          console.error('Failed to load preview:', err);
          setPreviewBlobUrl(null);
          setSpreadsheetHtml(null);
        }
      };

      fetchPreview();

      return () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        workbookRef.current = null;
        setSpreadsheetHtml(null);
        setSheetNames([]);
        setActiveSheet('');
      };
    }, [previewDocument, token]);

    // Exit early if no document
    if (!previewDocument) return null;

    return (
      <Dialog open={isPreviewModalOpen} onOpenChange={(open: boolean) => {
        setIsPreviewModalOpen(open);
        if (!open) setPreviewDocument(null);
      }}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col p-6">
          <DialogHeader>
            <DialogTitle>Preview: {previewDocument.name || 'Document'}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 flex flex-col min-h-0">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 shrink-0">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="font-medium text-gray-700">Type:</span><span className="ml-2 text-gray-900 uppercase">{previewDocument.file_extension}</span></div>
                <div><span className="font-medium text-gray-700">Size:</span><span className="ml-2 text-gray-900">{formatSize(previewDocument.file_size_bytes)}</span></div>
              </div>
            </div>

            <div className="border border-gray-300 rounded-lg overflow-hidden bg-gray-100 flex-1 relative flex items-center justify-center">
              {isImage && previewBlobUrl && <img src={previewBlobUrl} alt={previewDocument.name || 'Preview'} className="max-w-full max-h-full object-contain" />}
              {isPDF && previewBlobUrl && (
                <iframe
                  src={previewBlobUrl}
                  className="w-full h-full"
                  title="PDF Preview"
                />
              )}
              {isText && previewBlobUrl && <iframe src={previewBlobUrl} className="w-full h-full bg-white" title="Text Preview" />}
              {isSpreadsheet && spreadsheetHtml && (
                <div className="w-full h-full flex flex-col bg-white">
                  {sheetNames.length > 1 && (
                    <div className="flex border-b border-gray-200 bg-gray-50 px-2 pt-2 gap-1 shrink-0 overflow-x-auto">
                      {sheetNames.map((name) => (
                        <button
                          key={name}
                          onClick={() => handleSheetChange(name)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-t-md border border-b-0 transition-colors ${
                            activeSheet === name
                              ? 'bg-white text-gray-800 border-gray-300'
                              : 'bg-gray-100 text-gray-500 border-transparent hover:bg-gray-200'
                          }`}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  )}
                  <div
                    className="flex-1 overflow-auto p-2 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1 [&_td]:text-xs [&_td]:text-gray-700 [&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-100 [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-gray-700"
                    dangerouslySetInnerHTML={{ __html: spreadsheetHtml }}
                  />
                </div>
              )}
              {!isImage && !isPDF && !isText && !isSpreadsheet && (
                <div className="text-center text-gray-500 p-8">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-sm font-medium">Preview not available for this file type</p>
                  <p className="text-xs mt-2">Click download to view the file</p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2 shrink-0">
            <Button onClick={() => { setIsPreviewModalOpen(false); setPreviewDocument(null); }} variant="outline">Close</Button>
            <Button onClick={() => handleDownload(previewDocument.doc_id, previewDocument.name)} className="flex items-center gap-2">
              <Download className="w-4 h-4" />Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  const handleSelectDocument = (docId: string) => {
    setSelectedDocuments(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  React.useEffect(() => {
    const close = () => setOpenFolderMenu(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  React.useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  React.useEffect(() => {
    setSelectedDocuments([]);
  }, [selectedFolder]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (actionsMenuRef.current && !actionsMenuRef.current.contains(target)) {
        setShowActionsMenu(null);
      }

      if (moveMenuRef.current && !moveMenuRef.current.contains(target)) {
        setShowMoveMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActionsMenu, showMoveMenu]);

  // Fetch Folder Permissions & Calculate Access
  React.useEffect(() => {
    const fetchPermissions = async () => {
      if (!user) return;

      setCanRenameFolder(false);
      setCanDeleteFolder(false);
      setCanUpload(false);
      setCanCreateFolder(false);

      // At root ('all'), allow creating root folders but hide upload.
      if (selectedFolder === 'all') {
        setCanCreateFolder(true);
        setCanUpload(false);
        return;
      }

      if (selectedFolder !== 'new-folder') {
        const currentFolder = currentFolderInfo;

        // RELATION folders: both participants can upload/manage
        if (currentFolder?.folder_type === 'RELATION') {
          setCanUpload(true);
          setCanCreateFolder(true);
          setCanDeleteDocuments(true);  // Allow deleting documents in shared folders
          setCanRenameFolder(false);  // Don't rename system shared folders
          setCanDeleteFolder(false);  // Don't delete system shared folders
          return;
        }

        // PRIVATE folders: only owner can manage
        if (currentFolder?.folder_type === 'PRIVATE' || currentFolder?.is_private) {
          setCanUpload(true);
          setCanRenameFolder(true);
          setCanDeleteFolder(true);
          setCanCreateFolder(true);
          return;
        }

        // ORGANIZATION folders: use V2 API to get current user's permissions
        try {
          const folderId = currentFolder?.folder_id || selectedFolder;
          console.log('📋 Fetching V2 user permissions for folder:', folderId, 'folder_type:', currentFolder?.folder_type);
          const response = await getFolderPermissionForTheUser(String(folderId));
          console.log('📋 V2 permissions response:', response);
          if (response.success && response.data) {
            const perm = response.data;
            console.log('📋 Setting permissions - canUpload:', perm.can_create, 'canRename:', perm.can_update, 'canDelete:', perm.can_delete);
            setCanUpload(perm.can_create ?? false);
            setCanCreateFolder(perm.can_create ?? false);
            setCanRenameFolder(perm.can_update ?? false);
            setCanDeleteFolder(perm.can_delete ?? false);
            setCanDeleteDocuments(perm.can_delete ?? false);
          }
        } catch (error) {
          console.error("Failed to fetch user permissions", error);
          // Fallback for private subfolders
          if (currentFolder?.is_private) {
            setCanUpload(true);
            setCanRenameFolder(true);
          }
        }
      }
    };

    fetchPermissions();
  }, [selectedFolder, user, foldersFromServer, currentFolderInfo]);

  // Fetch documents when folder changes
  React.useEffect(() => {
    const fetchFolderDocs = async () => {
      if (!selectedFolder || selectedFolder === 'all' || selectedFolder === 'new-folder') return;

      const folderInfo = foldersFromServer.find(f => String(f.folder_id) === selectedFolder)
        || childFolders.find(f => String(f.folder_id) === selectedFolder)
        || folderStack.find(f => f.id === selectedFolder);

      // Prefer UUID for fetch, fallback to selectedFolder (ID) if not found
      // Verify: Logic matches `currentFolderInfo` but safe to recalc here or use memo
      const info = folderInfo as any;
      const folderUUID = info ? (info.folder_uuid || info.uuid || selectedFolder) : selectedFolder;

      try {
        setLoading(true);
        const response: any = await getFolderDocuments(String(folderUUID));

        if (response && response.data) {
          const rawDocs = response.data;
          const newDocs: DocustorDocumentUI[] = rawDocs.map((d: any) => ({
            doc_id: d.doc_uuid,
            name: d.document_name ?? d.original_filename,
            folder_id: String(selectedFolder), // Keep internal ID reference
            file_type: d.file_extension ?? d.file_type ?? '',
            file_extension: d.file_extension ?? '',
            file_size_bytes: d.file_size_bytes ?? 0,
            created_at: d.document_created_at ?? d.created_at,
            created_by: d.created_by,
          }));

          setDocuments(newDocs);
        } else {
          setDocuments([]);
        }
      } catch (error) {
        console.error("Failed to fetch folder documents", error);
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFolderDocs();
  }, [selectedFolder]);

  return (
    <div className="p-4">
      <PreviewModal />

      {/* ── Top Bar: Search & Actions ── */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search folders or files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 pl-9 text-sm border border-slate-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <MagnifyingGlass size={16} />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* New Folder Button */}
          {canCreateFolder && (
            <button
              onClick={() => {
                if (selectedFolder === 'all') {
                  setIsCreateFolderOpen(true);
                } else {
                  setNewSubFolderName('');
                  setIsCreateSubFolderOpen(true);
                }
              }}
              className="flex items-center justify-center gap-2 bg-white text-[#4b45e5] px-3 py-2 rounded-lg h-9 text-sm font-medium border border-[#4b45e5] hover:bg-[#4b45e5]/5 transition-colors"
            >
              <FolderPlus size={16} weight="regular" />
              <span className="whitespace-nowrap">
                {selectedFolder === 'all' ? 'New Folder' : 'New Subfolder'}
              </span>
            </button>
          )}

          {/* Upload Button */}
          {canUpload && (
            <button
              onClick={() => {
                const targetFolderId = (selectedFolder && selectedFolder !== 'all') ? selectedFolder : '';
                if (targetFolderId) {
                  setUploadData(prev => ({ ...prev, folderId: targetFolderId }));
                  setShowUploadModal(true);
                } else {
                  // If at root, try to default to first folder or just open modal (which might need a folder pre-selected)
                  // If foldersFromServer has items, pick the first one.
                  if (foldersFromServer.length > 0) {
                    const firstId = String(foldersFromServer[0].folder_id);
                    setUploadData(prev => ({ ...prev, folderId: firstId }));
                    setShowUploadModal(true);
                    showToast.info("Uploading to " + foldersFromServer[0].name);
                  } else {
                    showToast.error('Please create a folder first');
                  }
                }
              }}
              className="flex items-center justify-center gap-2 bg-[#4b45e5] text-white px-3 py-2 rounded-lg hover:bg-[#4b45e5]/90 transition-colors h-9 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={16} weight="regular" />
              <span className="whitespace-nowrap">Upload File</span>
            </button>
          )}

          {/* Folder Actions Menu */}
          {selectedFolder !== 'all' && (canRenameFolder || canDeleteFolder) && (
            <div className="relative" ref={null /* Using verify click outside global listener */}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenFolderMenu(!openFolderMenu);
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors border border-transparent"
              >
                <DotsThreeVertical size={20} weight="bold" />
              </button>
              {openFolderMenu && (
                <div
                  className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {canRenameFolder && (
                    <button
                      onClick={async () => {
                        const f = currentFolderInfo;
                        setOpenFolderMenu(false);

                        // Load current permissions from API
                        let currentPermissions = DEFAULT_PERMISSIONS_MATRIX;
                        if (f?.folder_type === 'ORGANIZATION') {
                          try {
                            const folderUUID = f.folder_uuid || f.folder_id;
                            const response = await getFolderPermissions(String(folderUUID));
                            if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
                              currentPermissions = response.data.map((p: any) => ({
                                role_type: p.role_type,
                                can_create: p.can_create,
                                can_read: p.can_read,
                                can_update: p.can_update,
                                can_delete: p.can_delete,
                              }));
                            }
                          } catch (e) {
                            console.error('Failed to load folder permissions', e);
                          }
                        }

                        setEditFolderForm({
                          name: f?.name || '',
                          description: f?.description || '',
                          max_file_size_mb: f?.max_file_size_mb || 20,
                          allowed_extensions: f?.allowed_extensions || [],
                          permissions: currentPermissions,
                        });
                        setIsEditFolderOpen(true);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <PencilSimple size={16} /> Edit Folder
                    </button>
                  )}
                  {canDeleteFolder && (
                    <button
                      onClick={() => {
                        const f = currentFolderInfo;
                        setFolderToDelete({ id: String(f?.folder_uuid || f?.folder_id || selectedFolder), name: f?.name || '' });
                        setIsDeleteFolderOpen(true);
                        setOpenFolderMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash size={16} /> Delete Folder
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Breadcrumbs & Navigation ── */}
      <div className="mb-6">
        {selectedFolder === 'all' ? (
          <h2 className="text-lg font-semibold text-slate-900">My Documents</h2>
        ) : (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            {/* Back button — visible when inside any folder */}
            {folderStack.length > 0 && (
              <button
                onClick={() => navigateToStackIndex(folderStack.length - 2)}
                className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-slate-100 text-slate-400 hover:text-[#4b45e5] transition-colors mr-1"
                title="Go back"
              >
                <CaretLeft size={18} weight="bold" />
              </button>
            )}
            <button
              onClick={() => {
                setFolderStack([]);
                setSelectedFolder('all');
                setSearchTerm('');
              }}
              className="hover:text-[#4b45e5] flex items-center gap-1"
            >
              <Folder size={16} className="text-[#4b45e5]" />
              <span>All Documents</span>
            </button>

            {folderStack.map((item, index) => (
              <React.Fragment key={item.id}>
                <CaretRight size={12} className="flex-shrink-0" />
                <button
                  onClick={() => navigateToStackIndex(index)}
                  className={`hover:text-[#4b45e5] truncate max-w-[200px] ${index === folderStack.length - 1 ? 'font-semibold text-slate-900 pointer-events-none' : ''}`}
                  title={item.name}
                >
                  {item.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* ── Root Folders Grid (Categorized) ── */}
      {/* Inline error banner for delete failures */}
      {deleteError && (
        <div className="mb-4 flex items-center justify-between bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="text-red-500 hover:text-red-700 ml-4 font-bold">&times;</button>
        </div>
      )}

      {selectedFolder === 'all' && (() => {
        const filtered = foldersFromServer.filter(f => getFolderDisplayName(f).toLowerCase().includes(searchTerm.toLowerCase()));
        const organizationFolders = filtered.filter(f => f.folder_type === 'ORGANIZATION');
        const privateFolders = filtered.filter(f => f.folder_type === 'PRIVATE');
        const sharedFolders = filtered.filter(f => f.folder_type === 'RELATION');

        const renderFolderCard = (folder: DocustorFolderUI) => {
          const displayName = getFolderDisplayName(folder);
          const isSystemFolder = folder.description?.startsWith('shared system');
          return (
            <div
              key={folder.folder_id}
              onClick={() => navigateIntoFolder(folder)}
              className="group relative bg-white p-4 rounded-xl border border-slate-200 hover:border-[#4b45e5] hover:shadow-md transition-all cursor-pointer flex flex-col gap-3"
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-[#4b45e5]/10 text-[#4b45e5] flex items-center justify-center group-hover:bg-[#4b45e5] group-hover:text-white transition-colors">
                  <Folder size={24} weight="fill" />
                </div>
                <div className="flex items-center gap-1">
                  {folder.folder_type === 'PRIVATE' && (
                    <div className="bg-amber-100 text-amber-700 p-1 rounded-md" title="Private Folder">
                      <Lock size={12} weight="bold" />
                    </div>
                  )}
                  {/* Delete is only available from the action menu inside the folder, where permissions are checked */}
                </div>
              </div>

              <div className="flex items-end justify-between">
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate" title={displayName}>{displayName}</h3>
                  <p className="text-xs text-slate-400 mt-1">Click to open</p>
                </div>
                <CaretRight size={16} className="text-slate-300 group-hover:text-[#4b45e5] transition-colors flex-shrink-0" />
              </div>
            </div>
          );
        };

        return (
          <div className="space-y-6 mb-8">
            {organizationFolders.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 tracking-wider mb-3">Organization Folders</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {organizationFolders.map(renderFolderCard)}
                </div>
              </div>
            )}
            {privateFolders.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 tracking-wider mb-3">Private Folders</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {privateFolders.map(renderFolderCard)}
                </div>
              </div>
            )}
            {sharedFolders.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 tracking-wider mb-3">Shared Folders</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {sharedFolders.map(renderFolderCard)}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Subfolders Grid ── */}
      {selectedFolder !== 'all' && childFolders.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-slate-500 tracking-wider mb-3">Folders</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {childFolders
              .filter(f => f.name.toLowerCase().includes(searchTerm.toLowerCase()))
              .map(folder => {
                const displayName = getFolderDisplayName(folder);
                return (
                  <div
                    key={folder.folder_id}
                    onClick={() => navigateIntoFolder(folder)}
                    className="group bg-white p-3 rounded-lg border border-slate-200 hover:border-[#4b45e5] hover:shadow-sm transition-all cursor-pointer flex items-center gap-3"
                  >
                    <div className="text-[#4b45e5]">
                      <Folder size={20} weight="fill" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 text-sm truncate" title={displayName}>{displayName}</p>
                      {(folder.document_count ?? 0) > 0 && (
                        <p className="text-xs text-slate-400">{folder.document_count} {folder.document_count === 1 ? 'file' : 'files'}</p>
                      )}
                    </div>
                    {(folder.is_private || folder.folder_type === 'PRIVATE') && (
                      <div className="bg-amber-100 text-amber-700 p-1 rounded-md flex-shrink-0" title="Private Folder">
                        <Lock size={12} weight="bold" />
                      </div>
                    )}
                    <CaretRight size={14} className="text-slate-300 group-hover:text-[#4b45e5] transition-colors flex-shrink-0" />
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* ── Documents Table ── */}
      {selectedFolder !== 'all' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-visible">
          {/* Table Header */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-1 px-2 py-1 h-10 items-center">
              <div className="col-span-1"></div>
              <div className="col-span-3 flex items-center text-sm font-medium text-slate-500 text-left whitespace-nowrap overflow-hidden">Name</div>
              <div className="col-span-2 flex items-center justify-center text-sm font-medium text-slate-500 whitespace-nowrap overflow-hidden">Type</div>
              <div className="col-span-3 flex items-center justify-center text-sm font-medium text-slate-500 text-left whitespace-nowrap overflow-hidden">Created By</div>
              <div className="col-span-2 flex items-center justify-end text-sm font-medium text-slate-500 whitespace-nowrap overflow-hidden">Creation Date</div>
              <div className="col-span-1 flex items-center justify-center"></div>
            </div>
          </div>

          {/* Table Body */}
          {paginatedDocuments.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center border-b border-gray-200 min-h-[300px]">
              {/* Empty State */}
              <FileIcon size={48} className="text-gray-400 mb-4" weight="regular" />
              <p className="text-sm font-medium text-gray-900 mb-2">No documents found</p>
              {searchTerm ? (
                <p className="text-xs text-gray-500">Try adjusting your search criteria</p>
              ) : (
                <p className="text-xs text-gray-500">Upload a file to get started</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {paginatedDocuments.map((document) => (
                <div key={String(document.doc_id)} className="grid grid-cols-12 gap-1 px-2 py-2 h-10 items-center hover:bg-gray-50">
                  <div className="col-span-1 flex items-center justify-center">
                    <Checkbox
                      checked={selectedDocuments.includes(String(document.doc_id))}
                      onCheckedChange={() => handleSelectDocument(String(document.doc_id))}
                    />
                  </div>
                  <div className="col-span-3 flex items-center gap-1 min-w-0 max-w-full">
                    <div className="min-w-0 flex-1">
                      <CellTooltip value={document.name}>
                        <span
                          className="text-sm text-slate-900 font-medium cursor-pointer hover:underline hover:text-blue-600 truncate block"
                          onClick={() => handlePreview(document)}
                        >
                          {document.name}
                        </span>
                      </CellTooltip>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center justify-center">
                    <CellTooltip value={getFileTypeLabel(document.file_type, document.name)}>
                      <div className="flex items-center gap-1 min-w-0">
                        {getFileIcon(document.file_type, document.name)}
                        <span className="text-sm text-slate-500 truncate">
                          {getFileTypeLabel(document.file_type, document.name)}
                        </span>
                      </div>
                    </CellTooltip>
                  </div>
                  <div className="col-span-3 flex items-center justify-center gap-1.5 min-w-0 max-w-full">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-medium text-slate-700">
                        {(document.created_by || 'U')[0]}
                      </span>
                    </div>
                    <CellTooltip value={document.created_by || 'Unknown'}>
                      <span className="text-sm text-slate-900 truncate">{document.created_by || 'Unknown'}</span>
                    </CellTooltip>
                  </div>
                  <div className="col-span-2 flex items-center justify-end min-w-0">
                    <CellTooltip value={getCreationDate(document)}>
                      <span className="text-sm text-slate-500">{getCreationDate(document)}</span>
                    </CellTooltip>
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    {/* Row Actions */}
                    <div className="relative" ref={actionsMenuRef}>
                      <button onClick={(e) => { e.stopPropagation(); setShowActionsMenu(showActionsMenu === String(document.doc_id) ? null : String(document.doc_id)); }} className="p-1 hover:bg-gray-200 rounded">
                        <DotsThreeVertical size={20} className="text-gray-400" />
                      </button>
                      {showActionsMenu === String(document.doc_id) && (
                        <div className="absolute right-0 top-8 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[10000]">
                          <div className="py-1">
                            <button
                              onMouseDown={(e) => { e.stopPropagation(); handleDownload(String(document.doc_id), document.name); setShowActionsMenu(null); }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <Download size={16} /> Download
                            </button>
                            {canDeleteDocuments && (
                              <button
                                onMouseDown={(e) => { e.stopPropagation(); handleDelete(document.doc_id, document.folder_id, document.name); setShowActionsMenu(null); }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Trash size={16} /> Delete
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Table Footer / Pagination */}
          {paginatedDocuments.length > 0 && (
            <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="text-xs text-zinc-500">{filteredDocuments.length} document(s)</div>
                <div className="flex items-center gap-2">
                  {/* Page Size & Pagination controls - simplified */}
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"><CaretLeft size={16} /></button>
                    <span className="text-xs">Page {currentPage} of {totalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"><CaretRight size={16} /></button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )
      }

      {/* ── Modals ── */}

      {/* Upload Modal - keeping existing logic */}
      <Dialog open={showUploadModal} onOpenChange={(open: boolean) => !open && handleCloseUploadModal()}>
        <DialogContent className="max-w-xl p-0 bg-white gap-0">
          {/* ... existing upload modal content ... */}
          {/* I will inline the essential parts or assume reuse if efficient, but I'll write it out to be safe */}
          <div className="p-6 border-b border-gray-100 bg-gray-50/50">
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
            </DialogHeader>
          </div>
          <div className="p-6 space-y-6">
            <div
              className={`relative border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center transition-all duration-200 group cursor-pointer ${uploadFile ? 'border-emerald-400 bg-emerald-50/30' : 'border-slate-300 bg-slate-50'}`}
              onDrop={handleDrop} onDragOver={handleDragOver}
            >
              {!uploadFile && !isBusy && (
                <input
                  type="file"
                  ref={fileInputRef}
                  accept={acceptedFileTypes}
                  onChange={handleFileUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
              )}
              {uploadFile ? (
                <div className="flex flex-col items-center">
                  <CheckCircle size={24} className="text-emerald-500 mb-2" weight="fill" />
                  <p className="font-semibold">{uploadFile.name}</p>
                  <button onClick={(e) => { e.preventDefault(); setUploadFile(null); }} className="text-red-500 text-xs mt-2">Remove</button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload size={24} className="text-indigo-500 mb-2" />
                  <p className="text-sm">Click to upload or drag and drop</p>
                  {uploadTargetFolder?.allowed_extensions?.length ? (
                    <p className="text-xs text-slate-400 mt-1">Allowed: {uploadTargetFolder.allowed_extensions.map(e => e.toUpperCase()).join(', ')}</p>
                  ) : (
                    <p className="text-xs text-slate-400 mt-1">All file types accepted</p>
                  )}
                  {uploadTargetFolder?.max_file_size_mb && (
                    <p className="text-xs text-slate-400">Max size: {uploadTargetFolder.max_file_size_mb} MB</p>
                  )}
                </div>
              )}
            </div>

            {/* Optional Rename */}
            {uploadFile && (
              <div>
                <label className="text-xs font-semibold mb-1 block">Display Name</label>
                <input type="text" value={uploadData.name} onChange={e => setUploadData(d => ({ ...d, name: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
            )}
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
            <button onClick={handleCloseUploadModal} className="px-4 py-2 text-sm bg-white border rounded">Cancel</button>
            <button onClick={handleUpload} disabled={!uploadFile || isBusy} className="px-4 py-2 text-sm bg-[#4b45e5] text-white rounded disabled:opacity-50">
              {isBusy ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Folder Modal */}
      <Dialog
        open={isCreateFolderOpen}
        onOpenChange={(open) => {
          setIsCreateFolderOpen(open);
          if (!open) {
            setNewFolderName('');
            setNewFolderIsPrivate(true);
            setNewFolderPermissions(DEFAULT_PERMISSIONS_MATRIX);
            setNewFolderMaxFileSizeMb(20);
            setNewFolderAllowedExtensions([]);
            setShowNewFolderExtensionDropdown(false);
          }
        }}
      >
        <DialogContent className={user?.is_superadmin && !newFolderIsPrivate ? 'max-w-lg' : 'max-w-sm'}>
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {user?.is_superadmin
                ? 'Create a private folder for personal use, or an organization folder visible to your team based on role permissions.'
                : 'Create a private folder to store your personal documents. Only you will have access.'}
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 space-y-4">
            <input type="text" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" placeholder="Folder Name" />

            {/* Privacy toggle — only shown to SuperAdmin; all other roles always get PRIVATE */}
            {user?.is_superadmin && (
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={newFolderIsPrivate}
                  onCheckedChange={(checked: boolean | 'indeterminate') => setNewFolderIsPrivate(!!checked)}
                />
                <label className="text-sm text-slate-600">Private (only you can see this folder)</label>
              </div>
            )}

            {/* File upload settings — only relevant for organization folders */}
            {user?.is_superadmin && !newFolderIsPrivate && <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Max File Size (MB)</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={newFolderMaxFileSizeMb}
                  onChange={(e) => setNewFolderMaxFileSizeMb(Number(e.target.value) || 20)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#4b45e5] focus:ring-1 focus:ring-[#4b45e5] outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Allowed File Types</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowNewFolderExtensionDropdown(!showNewFolderExtensionDropdown)}
                    className="w-full flex items-center justify-between px-3 py-2 border border-slate-300 rounded-md text-sm bg-white hover:border-slate-400 transition"
                  >
                    <span className="text-slate-700 truncate">
                      {newFolderAllowedExtensions.length === 0 ? 'All types' : `${newFolderAllowedExtensions.length} selected`}
                    </span>
                    <CaretDown size={14} className="text-slate-400 shrink-0 ml-1" />
                  </button>
                  {showNewFolderExtensionDropdown && (
                    <div className="absolute z-50 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg p-2 max-h-52 overflow-y-auto">
                      <div className="flex gap-2 pb-2 mb-2 border-b border-slate-100">
                        <button type="button" className="text-xs text-[#4b45e5] hover:underline px-1" onClick={() => setNewFolderAllowedExtensions([...AVAILABLE_EXTENSIONS])}>Select All</button>
                        <button type="button" className="text-xs text-slate-500 hover:underline px-1" onClick={() => setNewFolderAllowedExtensions([])}>Clear</button>
                      </div>
                      {AVAILABLE_EXTENSIONS.map(ext => {
                        const isSelected = newFolderAllowedExtensions.includes(ext);
                        return (
                          <div
                            key={ext}
                            className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer"
                            onClick={() => setNewFolderAllowedExtensions(prev =>
                              isSelected ? prev.filter(e => e !== ext) : [...prev, ext]
                            )}
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#4b45e5] border-[#4b45e5]' : 'border-slate-300'}`}>
                              {isSelected && <Check size={10} weight="bold" className="text-white" />}
                            </div>
                            <span className="text-sm uppercase text-slate-700">{ext}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>}

            {/* Contextual info tip */}
            {user?.is_superadmin ? (
              <div className={`flex items-start gap-2 rounded-md px-3 py-2.5 text-xs ${newFolderIsPrivate ? 'bg-slate-50 text-slate-600 border border-slate-200' : 'bg-indigo-50 text-indigo-700 border border-indigo-200'}`}>
                <span className="mt-0.5 shrink-0 text-base leading-none">{newFolderIsPrivate ? '🔒' : '🌐'}</span>
                <span>
                  {newFolderIsPrivate
                    ? 'This folder will be private — only you can view or manage its contents.'
                    : 'This folder will be visible across the organization. Use the permission matrix below to control what each role can do.'}
                </span>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md px-3 py-2.5 text-xs bg-slate-50 text-slate-600 border border-slate-200">
                <span className="mt-0.5 shrink-0 text-base leading-none">🔒</span>
                <span>This folder will be private — only you can view or manage its contents.</span>
              </div>
            )}

            {/* Permission matrix — only shown when SuperAdmin chooses to create a public (ORGANIZATION) folder */}
            {user?.is_superadmin && !newFolderIsPrivate && (
              <div className="border-t border-slate-200 pt-4">
                <label className="block text-sm font-semibold text-slate-800">Access Permissions</label>
                <p className="text-xs text-slate-500 mb-3">Set who can access files in this folder.</p>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-2.5 text-xs font-semibold text-[#4b45e5] uppercase tracking-wide">Role</th>
                        {PERMISSION_LEVELS.map((level) => (
                          <th key={level} className="px-4 py-2.5 text-xs font-semibold text-[#4b45e5] uppercase tracking-wide text-center">
                            {PERMISSION_LEVEL_CONFIG[level].label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {newFolderPermissions.map((p) => {
                        const currentLevel = derivePermissionLevel(p);
                        return (
                          <tr key={p.role_type} className="hover:bg-slate-50/50">
                            <td className="px-4 py-2.5 font-medium text-slate-700 text-sm">{ROLE_DISPLAY_NAMES[p.role_type] || p.role_type}</td>
                            {PERMISSION_LEVELS.map((level) => (
                              <td key={level} className="px-4 py-2.5 text-center">
                                <input
                                  type="radio"
                                  name={`new-folder-perm-${p.role_type}`}
                                  checked={currentLevel === level}
                                  onChange={() => {
                                    const levelPerms = PERMISSION_LEVEL_CONFIG[level].permissions;
                                    setNewFolderPermissions(prev =>
                                      prev.map(perm =>
                                        perm.role_type === p.role_type ? { ...perm, ...levelPerms } : perm
                                      )
                                    );
                                  }}
                                  className="w-4 h-4 text-[#4b45e5] focus:ring-[#4b45e5] cursor-pointer"
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="px-4 py-2 bg-slate-50 border-t text-xs text-slate-500">
                    <span className="font-medium">View Only</span> — read files &nbsp;|&nbsp; <span className="font-medium">Contributor</span> — read + upload &nbsp;|&nbsp; <span className="font-medium">Full Control</span> — all actions
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => {
                  setIsCreateFolderOpen(false);
                  setNewFolderName('');
                  setNewFolderIsPrivate(true);
                  setNewFolderPermissions(DEFAULT_PERMISSIONS_MATRIX);
                  setNewFolderMaxFileSizeMb(20);
                  setNewFolderAllowedExtensions([]);
                  setShowNewFolderExtensionDropdown(false);
                }}
                className="px-3 py-2 text-sm border rounded"
              >Cancel</button>
              <button
                disabled={!newFolderName.trim() || isBusy}
                onClick={() => {
                  safeAction(async () => {
                    try {
                      if (user?.is_superadmin && !newFolderIsPrivate) {
                        // SuperAdmin creating an organization folder — include permission matrix
                        await createDocustorFolder({
                          name: newFolderName,
                          folder_type: 'ORGANIZATION',
                          is_private: false,
                          description: '',
                          allowed_extensions: newFolderAllowedExtensions,
                          max_file_size_mb: newFolderMaxFileSizeMb,
                          folder_uuid: '',
                          permissions: newFolderPermissions,
                        });
                      } else {
                        // All other roles (or superadmin choosing private): create PRIVATE
                        await createDocustorFolder({
                          name: newFolderName,
                          folder_type: 'PRIVATE',
                          is_private: true,
                          description: '',
                          allowed_extensions: newFolderAllowedExtensions,
                          max_file_size_mb: newFolderMaxFileSizeMb,
                          folder_uuid: '',
                        });
                      }
                      await fetchDocuments();
                      setIsCreateFolderOpen(false);
                      setNewFolderName('');
                      setNewFolderIsPrivate(true);
                      setNewFolderPermissions(DEFAULT_PERMISSIONS_MATRIX);
                      setNewFolderMaxFileSizeMb(20);
                      setNewFolderAllowedExtensions([]);
                      setShowNewFolderExtensionDropdown(false);
                      showToast.success('Folder created successfully');
                    } catch (e) {
                      console.error(e);
                      showToast.error('Failed to create folder');
                    }
                  });
                }}
                className="px-3 py-2 text-sm bg-[#4b45e5] text-white rounded disabled:opacity-50"
              >Create</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Subfolder Modal */}
      <Dialog open={isCreateSubFolderOpen} onOpenChange={setIsCreateSubFolderOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Subfolder</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-2 pb-6">
            <input
              type="text"
              value={newSubFolderName}
              onChange={(e) => setNewSubFolderName(e.target.value)}
              placeholder="Subfolder name"
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
            />

            <div className="mt-3 flex items-center gap-2">
              <Checkbox
                checked={isSubFolderPrivate}
                onCheckedChange={(checked: boolean | 'indeterminate') => setIsSubFolderPrivate(!!checked)}
              />
              <label className="text-sm text-slate-600">
                Private (only you can see this folder)
              </label>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button className="px-3 py-2 text-sm border rounded" onClick={() => { setIsCreateSubFolderOpen(false); setIsSubFolderPrivate(false); }}>Cancel</button>
              <button
                className="px-3 py-2 text-sm bg-[#4b45e5] text-white rounded"
                disabled={!newSubFolderName || isBusy}
                onClick={() => {
                  const parent = currentFolderInfo;
                  if (!parent) return;
                  safeAction(async () => {
                    try {
                      const parentFetchId = parent.folder_uuid || parent.folder_id;

                      if (parent.folder_type === 'RELATION') {
                        // Use the relation subfolder API for RELATION parents
                        const parentUUID = parent.folder_uuid || parent.folder_id;
                        await createPrivateSubFolder(String(parentUUID), newSubFolderName, isSubFolderPrivate);
                      } else {
                        // Use generic folder creation for PRIVATE/ORGANIZATION parents
                        await createDocustorFolder({
                          name: newSubFolderName,
                          description: '',
                          allowed_extensions: parent.allowed_extensions || [],
                          max_file_size_mb: parent.max_file_size_mb || 20,
                          folder_type: isSubFolderPrivate ? 'PRIVATE' : 'ORGANIZATION',
                          is_private: isSubFolderPrivate,
                          parent_folder_id: String(parent.folder_id),
                          folder_uuid: ''
                        });
                      }

                      // Refresh child folders to ensure we have the correct numeric ID (critical for uploads)
                      const freshChildren = await getFolderChildren(String(parentFetchId));
                      const mappedChildren: DocustorFolderUI[] = (freshChildren?.data || []).map((f: any) => ({
                        folder_id: f.id ?? f.folder_id,
                        folder_uuid: f.folder_uuid,
                        name: f.name,
                        document_count: 0,
                        allowed_extensions: f.allowed_extensions || [],
                        max_file_size_mb: f.max_file_size_mb || 20,
                        is_private: f.is_private || f.folder_type === 'PRIVATE',
                        folder_type: f.folder_type || (f.is_private ? 'PRIVATE' : 'ORGANIZATION'),
                        rec_user_id: f.rec_user_id,
                        created_by: undefined,
                        parent_folder_id: f.parent_folder_id ? String(f.parent_folder_id) : null,
                        id: f.id
                      }));
                      setChildFolders(mappedChildren);
                      setIsCreateSubFolderOpen(false);
                      setNewSubFolderName(''); // Clear input
                      showToast.success('Subfolder created successfully');
                    } catch (err) {
                      console.error("Failed to create subfolder", err);
                      showToast.error('Failed to create subfolder');
                    }
                  });
                }}
              >Create</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Folder Settings Modal */}
      <Dialog open={isEditFolderOpen} onOpenChange={setIsEditFolderOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Folder</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-2 pb-6 space-y-5">
            {/* Folder Name & Max File Size */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Folder Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={editFolderForm.name}
                  onChange={(e) => {
                    const val = e.target.value;
                    const capitalized = val.charAt(0).toUpperCase() + val.slice(1);
                    setEditFolderForm(prev => ({ ...prev, name: capitalized }));
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm shadow-sm focus:border-[#4b45e5] focus:ring-1 focus:ring-[#4b45e5] outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Max File Size (MB)</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={editFolderForm.max_file_size_mb}
                  onChange={(e) => setEditFolderForm(prev => ({ ...prev, max_file_size_mb: Number(e.target.value) || 20 }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm shadow-sm focus:border-[#4b45e5] focus:ring-1 focus:ring-[#4b45e5] outline-none"
                />
              </div>
            </div>

            {/* Allowed File Types Dropdown */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Allowed File Types</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowExtensionDropdown(!showExtensionDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-slate-300 rounded-md text-sm bg-white shadow-sm hover:border-slate-400 transition"
                >
                  <span className="text-slate-700">
                    {editFolderForm.allowed_extensions.length === 0
                      ? 'All types allowed'
                      : `${editFolderForm.allowed_extensions.length} selected`}
                  </span>
                  <CaretDown size={16} className="text-slate-400" />
                </button>
                {showExtensionDropdown && (
                  <div className="absolute z-50 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-2 max-h-60 overflow-y-auto">
                    <div className="flex gap-2 pb-2 mb-2 border-b border-slate-100">
                      <button
                        type="button"
                        className="text-xs text-[#4b45e5] hover:underline px-1"
                        onClick={() => setEditFolderForm(prev => ({ ...prev, allowed_extensions: [...AVAILABLE_EXTENSIONS] }))}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        className="text-xs text-slate-500 hover:underline px-1"
                        onClick={() => setEditFolderForm(prev => ({ ...prev, allowed_extensions: [] }))}
                      >
                        Clear
                      </button>
                    </div>
                    {AVAILABLE_EXTENSIONS.map(ext => {
                      const isSelected = editFolderForm.allowed_extensions.includes(ext);
                      return (
                        <div
                          key={ext}
                          className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer"
                          onClick={() => {
                            setEditFolderForm(prev => ({
                              ...prev,
                              allowed_extensions: isSelected
                                ? prev.allowed_extensions.filter(e => e !== ext)
                                : [...prev.allowed_extensions, ext],
                            }));
                          }}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#4b45e5] border-[#4b45e5]' : 'border-slate-300'}`}>
                            {isSelected && <Check size={10} weight="bold" className="text-white" />}
                          </div>
                          <span className="text-sm uppercase text-slate-700">{ext}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Access Permissions - only visible to HR Managers and Super Admins */}
            {currentFolderInfo?.folder_type === 'ORGANIZATION' && (user?.is_superadmin || user?.is_hrmanager) && (
              <div className="border-t border-slate-200 pt-4">
                <label className="block text-sm font-semibold text-slate-800">Access Permissions</label>
                <p className="text-xs text-slate-500 mb-3">Permissions apply to files within this folder, not the folder structure itself.</p>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-2.5 text-xs font-semibold text-[#4b45e5] uppercase tracking-wide">Role</th>
                        {PERMISSION_LEVELS.map((level) => (
                          <th key={level} className="px-4 py-2.5 text-xs font-semibold text-[#4b45e5] uppercase tracking-wide text-center">
                            {PERMISSION_LEVEL_CONFIG[level].label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {editFolderForm.permissions.map((p) => {
                        const currentLevel = derivePermissionLevel(p);
                        return (
                          <tr key={p.role_type} className="hover:bg-slate-50/50">
                            <td className="px-4 py-2.5 font-medium text-slate-700 text-sm">{ROLE_DISPLAY_NAMES[p.role_type] || p.role_type}</td>
                            {PERMISSION_LEVELS.map((level) => (
                              <td key={level} className="px-4 py-2.5 text-center">
                                <input
                                  type="radio"
                                  name={`edit-perm-${p.role_type}`}
                                  checked={currentLevel === level}
                                  onChange={() => {
                                    const levelPerms = PERMISSION_LEVEL_CONFIG[level].permissions;
                                    setEditFolderForm(prev => ({
                                      ...prev,
                                      permissions: prev.permissions.map(perm =>
                                        perm.role_type === p.role_type ? { ...perm, ...levelPerms } : perm
                                      ),
                                    }));
                                  }}
                                  className="w-4 h-4 text-[#4b45e5] focus:ring-[#4b45e5] cursor-pointer"
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="px-4 py-2 bg-slate-50 border-t text-xs text-slate-500">
                    <span className="font-medium">View Only</span> — read files &nbsp;|&nbsp; <span className="font-medium">Contributor</span> — read + upload &nbsp;|&nbsp; <span className="font-medium">Full Control</span> — all actions
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-3 border-t border-slate-200">
              <button
                className="px-4 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50 font-medium text-slate-700 transition"
                onClick={() => { setIsEditFolderOpen(false); setShowExtensionDropdown(false); }}
              >
                Cancel
              </button>
              <button
                disabled={!editFolderForm.name.trim() || isEditLoading}
                className="px-5 py-2 text-sm bg-[#4b45e5] text-white rounded-md hover:bg-[#4b45e5]/90 disabled:opacity-50 font-medium transition"
                onClick={() => {
                  safeAction(async () => {
                    setIsEditLoading(true);
                    try {
                      const folder = currentFolderInfo;
                      if (!folder) return;
                      const folderUUID = String(folder.folder_uuid || folder.folder_id);

                      // Update folder settings
                      await updateDocustorFolder(folderUUID, {
                        name: editFolderForm.name,
                        description: editFolderForm.description,
                        allowed_extensions: editFolderForm.allowed_extensions,
                        max_file_size_mb: editFolderForm.max_file_size_mb,
                      });

                      // Update permissions if it's an organization folder
                      if (folder.folder_type === 'ORGANIZATION') {
                        await updateFolderPermissions(folderUUID, editFolderForm.permissions);
                      }

                      const updatedFields = {
                        name: editFolderForm.name,
                        description: editFolderForm.description,
                        allowed_extensions: editFolderForm.allowed_extensions,
                        max_file_size_mb: editFolderForm.max_file_size_mb,
                      };

                      // Update root folders list
                      setFoldersFromServer(prev => prev.map(f =>
                        String(f.folder_id) === selectedFolder ? { ...f, ...updatedFields } : f
                      ));

                      // Update child folders list
                      setChildFolders(prev => prev.map(f =>
                        String(f.folder_id) === selectedFolder ? { ...f, ...updatedFields } : f
                      ));

                      // Update folder info cache
                      if (folderInfoMapRef.current[selectedFolder]) {
                        folderInfoMapRef.current[selectedFolder] = {
                          ...folderInfoMapRef.current[selectedFolder],
                          ...updatedFields,
                        };
                      }

                      // Update folder stack (breadcrumb name)
                      setFolderStack(prev => prev.map(item =>
                        item.id === selectedFolder ? { ...item, name: editFolderForm.name } : item
                      ));

                      showToast.success('Folder updated');
                      setIsEditFolderOpen(false);
                      setShowExtensionDropdown(false);
                    } catch (e: any) {
                      const data = e?.response?.data;
                      const message = (typeof data === 'string' ? data : (data?.error || data?.message))
                        || (e?.response?.status === 403 ? 'Permission denied' : 'Failed to update folder');
                      showToast.error(message, { id: 'docustor-folder-error' });
                      setIsEditFolderOpen(false);
                      setShowExtensionDropdown(false);
                    } finally {
                      setIsEditLoading(false);
                    }
                  });
                }}
              >
                {isEditLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={isDeleteFolderOpen} onOpenChange={setIsDeleteFolderOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete Folder</DialogTitle></DialogHeader>
          <DialogDescription>
            Are you sure you want to delete <strong>{folderToDelete?.name}</strong>? This action cannot be undone.
          </DialogDescription>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setIsDeleteFolderOpen(false)} className="px-3 py-2 border rounded text-sm">Cancel</button>
            <button
              onClick={() => {
                if (!folderToDelete) return;
                safeAction(async () => {
                  try {
                    await deleteDocustorFolder(folderToDelete.id);

                    // Remove from root folders list
                    setFoldersFromServer(prev => prev.filter(f =>
                      String(f.folder_id) !== folderToDelete.id && String(f.folder_uuid) !== folderToDelete.id
                    ));

                    // Remove from child folders list
                    setChildFolders(prev => prev.filter(f =>
                      String(f.folder_id) !== folderToDelete.id && String(f.folder_uuid) !== folderToDelete.id
                    ));

                    // Clean up cache
                    delete folderInfoMapRef.current[folderToDelete.id];

                    // Navigate back to root if we're inside the deleted folder
                    const isCurrentFolder = selectedFolder === folderToDelete.id
                      || currentFolderInfo?.folder_uuid === folderToDelete.id;
                    if (isCurrentFolder) {
                      setFolderStack([]);
                      setSelectedFolder('all');
                    }

                    showToast.success('Folder deleted');
                    setIsDeleteFolderOpen(false);
                    setFolderToDelete(null);
                  } catch (e: any) {
                    const data = e?.response?.data;
                    const message = (typeof data === 'string' ? data : (data?.error || data?.message))
                      || (e?.response?.status === 403 ? 'Permission denied' : 'Failed to delete folder');
                    showToast.error(message, { id: 'docustor-folder-error' });
                    setIsDeleteFolderOpen(false);
                    setFolderToDelete(null);
                  }
                });
              }}
              className="px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition"
            >Delete</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete Document</DialogTitle></DialogHeader>
          <DialogDescription>Are you sure?</DialogDescription>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setIsDeleteConfirmOpen(false)} className="border rounded px-3 py-2 text-sm">Cancel</button>
            <button onClick={confirmDelete} className="bg-red-600 text-white rounded px-3 py-2 text-sm">Delete</button>
          </div>
        </DialogContent>
      </Dialog>


    </div >
  );
};

export default DocumentsSection;
